import React, { useState, useEffect } from 'react';
import { Amenity } from '../types/amenity';
import RichTextEditor from './RichTextEditor';
import PhotoGallery from './PhotoGallery';
import { getImages } from '../services/uploadService';
import '../styles/AmenityFormModal.css';

interface AmenityFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: AmenityFormData) => Promise<void>;
  amenity?: Amenity | null;
  workspaceId: string;
  projectId: string;
}

export interface AmenityFormData {
  title: string;
  category: string;
  description: string;
  imageIds?: string[];
}

const CATEGORY_OPTIONS = [
  'Health & Wellness',
  'Outdoor Living',
  'Recreation',
  'Workspace',
  'Hospitality',
  'Entertainment',
  'Parking & Storage',
  'Safety & Security'
];

const AmenityFormModal: React.FC<AmenityFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  amenity,
  workspaceId,
  projectId
}) => {
  const [formData, setFormData] = useState<AmenityFormData>({
    title: '',
    category: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // For new amenities, we'll use project as the entity for image uploads
  // and collect imageIds after upload
  const entityType = amenity ? 'amenity' : 'project';
  const entityId = amenity?._id || projectId;

  useEffect(() => {
    if (amenity) {
      setFormData({
        title: amenity.title || '',
        category: amenity.category || '',
        description: amenity.description || ''
      });
    } else {
      setFormData({
        title: '',
        category: '',
        description: ''
      });
    }
    setError('');
  }, [amenity, isOpen]);

  if (!isOpen) return null;

  const handleChange = (field: keyof AmenityFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    if (!formData.category.trim()) {
      setError('Category is required');
      return;
    }

    try {
      setSaving(true);
      setError('');

      // Fetch uploaded images to get their IDs
      let imageIds: string[] = [];
      try {
        const images = await getImages(workspaceId, {
          entityType,
          entityId,
          imageType: 'amenity'
        });
        imageIds = images.map(img => img._id);
      } catch (err) {
        console.error('Error fetching images:', err);
      }

      await onSave({
        ...formData,
        imageIds
      });
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save amenity');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      title: '',
      category: '',
      description: ''
    });
    setError('');
    onClose();
  };

  return (
    <div className="amenity-form-modal-overlay" onClick={handleCancel}>
      <div className="amenity-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="amenity-form-header">
          <h2>{amenity ? 'Edit Amenity' : 'Add New Amenity'}</h2>
          <button className="amenity-form-close" onClick={handleCancel}>
            ✕
          </button>
        </div>

        <div className="amenity-form-body">
          {error && (
            <div className="amenity-form-error">
              {error}
            </div>
          )}

          {/* Title */}
          <div className="amenity-form-field">
            <label>Title *</label>
            <input
              type="text"
              className="form-input"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Enter amenity title"
              disabled={saving}
            />
          </div>

          {/* Category */}
          <div className="amenity-form-field">
            <label>Category *</label>
            <select
              className="form-input"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              disabled={saving}
            >
              <option value="">Select a category</option>
              {CATEGORY_OPTIONS.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div className="amenity-form-field">
            <label>Description</label>
            <RichTextEditor
              content={formData.description}
              onChange={(content) => handleChange('description', content)}
              placeholder="Enter amenity description..."
            />
          </div>

          {/* Images */}
          <div className="amenity-form-field">
            <label>Images</label>
            <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
              {amenity ? 'Upload or manage images for this amenity' : 'Upload images (they will be associated with the amenity after saving)'}
            </div>
            <PhotoGallery
              workspaceId={workspaceId}
              entityType={entityType}
              entityId={entityId}
              imageType="amenity"
              allowMultiple={true}
            />
          </div>
        </div>

        <div className="amenity-form-footer">
          <button
            type="button"
            className="btn btn-secondary btn-md"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-md"
            onClick={handleSubmit}
            disabled={saving || !formData.title.trim() || !formData.category.trim()}
          >
            {saving ? 'Saving...' : amenity ? 'Update Amenity' : 'Add Amenity'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AmenityFormModal;
